import { useState, useEffect, useCallback } from "react";

/**
 * Hook that tracks whether the browser tab/page is currently visible.
 * Uses the Page Visibility API with a `pagehide` fallback for mobile Safari
 * (which fires before the browser suspends/discards the tab).
 *
 * @returns {{ isVisible: boolean }}
 */
export function usePageVisibility() {
  const [isVisible, setIsVisible] = useState(
    () => document.visibilityState === "visible",
  );

  const handleVisibilityChange = useCallback(() => {
    setIsVisible(document.visibilityState === "visible");
  }, []);

  const handlePageHide = useCallback(() => {
    setIsVisible(false);
  }, []);

  useEffect(() => {
    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("pagehide", handlePageHide);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("pagehide", handlePageHide);
    };
  }, [handleVisibilityChange, handlePageHide]);

  return { isVisible };
}
